/**
 * CoachingCanvas — Agent Builder for All Sports
 *
 * Three-mode agent builder where coaches, analysts, and team owners
 * can create, configure, and deploy custom AI agents using SLAM's real
 * backend agents via /api/agent/chat (SSE streaming).
 *
 * Modes:
 * 1. Template Gallery - Pre-built sport-specific agent templates
 * 2. Configurator     - Visual data source/tool/output builder
 * 3. Chat Builder     - Natural language agent interaction + inline testing
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Bot, Sparkles, Send, Loader2, ChevronRight,
  Target, Radio, Eye, Video, Watch, Clipboard, DollarSign,
  Users, Database, BarChart3, Scissors, FileText, Grid, Heart,
  Search, Calculator, Monitor, MessageSquare, Mail, FileDown,
  Plug, Film, Smartphone, Globe, BookOpen, Image, Upload,
  Circle, X, Play, Check, Wrench, Lock,
} from 'lucide-react';
import { C } from '../design-system/primitives/constants';
import { safeMd } from '../../lib/safeMd';
import streamAgentChat from '../../utils/streamAgentChat';
import MCPAppRenderer from '../chat/MCPAppRenderer';
import {
  AGENT_TEMPLATES, DATA_SOURCES, AGENT_TOOLS, OUTPUT_CHANNELS,
  SPORT_OPTIONS, getTemplatesForSport, getSourcesForSport, getToolsForSport,
  PILLAR_CONFIG, groupByPillar, DEFAULT_PROMPTS,
  CONFIGURE_TOUR_STEPS, BUILD_TOUR_STEPS,
  BUILDER_SYSTEM_PROMPT,
} from './canvasConfig';
import AgentFlowBuilder from './AgentFlowBuilder';
import AgentDeployFlow, { getDeployMode } from './AgentDeployFlow';
import AgentFlowMinimap from './AgentFlowMinimap';
import CanvasChatAttachment from './CanvasChatAttachment';
import DeveloperPortal from './DeveloperPortal';
import useGamificationRewards from '../../hooks/useGamificationRewards';
import useGuidedTour from '../../hooks/useGuidedTour';
import { useSportContext } from '../../hooks/useSportContext';
import GuidedTooltip from '../design-system/GuidedTooltip';

// ─── Icon Map ───────────────────────────────────────────────────────────────

const ICON_MAP = {
  Target, Radio, Eye, Video, Watch, Clipboard, DollarSign,
  Users, Database, BarChart3, Scissors, FileText, Grid, Heart,
  Search, Calculator, Monitor, MessageSquare, Mail, FileDown,
  Plug, Film, Smartphone, Globe, BookOpen, Image, Upload, Circle,
  Bot, Wrench, Lock,
};

function getIcon(name) {
  return ICON_MAP[name] || Circle;
}

// streamAgentChat imported from ../../utils/streamAgentChat

const API_BASE = process.env.REACT_APP_API_URL || '';

// ─── Main Component ─────────────────────────────────────────────────────────

const VALID_SPORTS = ['basketball', 'baseball', 'soccer', 'hockey', 'football', 'golf'];

export default function CoachingCanvas() {
  // Detect sport from URL path or persisted context
  const location = useLocation();
  const { sportCategory } = useSportContext();
  const sportFromUrl = location.pathname.split('/')[1];
  const initialSport = VALID_SPORTS.includes(sportFromUrl)
    ? sportFromUrl
    : sportCategory || 'basketball';

  // Core state
  const [sport, setSport] = useState(initialSport);
  const [mode, setMode] = useState('templates'); // templates | configure | build | chat | develop
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Agent configuration
  const [agentConfig, setAgentConfig] = useState({
    name: '',
    description: '',
    sources: [],
    tools: [],
    outputs: ['chat'],
  });

  // Chat builder state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatAttachments, setChatAttachments] = useState([]);
  const [isChatStreaming, setIsChatStreaming] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [mcpArtifacts, setMcpArtifacts] = useState([]);

  // Preview / test state
  const [activeAgent, setActiveAgent] = useState(null); // { id, name, description, sport, tools }
  const [testMessages, setTestMessages] = useState([]);
  const [testInput, setTestInput] = useState('');
  const [isTestStreaming, setIsTestStreaming] = useState(false);
  const [testSessionId, setTestSessionId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Backend agents
  const [backendAgents, setBackendAgents] = useState([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);

  // Semantic Builder state
  const [builderInput, setBuilderInput] = useState('');
  const [builderMessages, setBuilderMessages] = useState([]);
  const [isBuilding, setIsBuilding] = useState(false);
  const [builtAgent, setBuiltAgent] = useState(null);
  const [savedAgents, setSavedAgents] = useState([]);
  const builderEndRef = useRef(null);

  // Refs
  const chatEndRef = useRef(null);
  const testEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const abortRef = useRef(null);

  // Gamification — fire explore_canvas quest on mount
  const { rewardCanvasExplore, rewardAgentCreation, rewardTemplateSelect } = useGamificationRewards();

  // Deploy flow state
  const [showDeployFlow, setShowDeployFlow] = useState(false);

  // Guided tour for first-time canvas visitors
  const canvasTour = useGuidedTour('canvas', [
    { targetSelector: '[data-tour="canvas-templates"]', title: 'Template Gallery', body: 'Browse pre-built agent templates for your sport. Pick one to get started quickly.', placement: 'bottom' },
    { targetSelector: '[data-tour="canvas-mode"]', title: 'Build Your Agent', body: 'Switch between Playbook templates, visual Configure mode, Build Agent with chat, or test via Chat.', placement: 'bottom' },
  ]);
  const configureTour = useGuidedTour('canvas-configure', CONFIGURE_TOUR_STEPS);
  const buildTour = useGuidedTour('canvas-build', BUILD_TOUR_STEPS);

  // Start tours when entering relevant modes
  useEffect(() => {
    if (mode === 'configure') configureTour.startTour();
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (mode === 'build') buildTour.startTour();
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { rewardCanvasExplore(); }, [rewardCanvasExplore]);

  // ─── Fetch real agents from backend ──────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    setIsLoadingAgents(true);

    fetch(`${API_BASE}/api/agent/agents?sport=${sport}`)
      .then(r => r.json())
      .then(data => {
        if (!cancelled) {
          setBackendAgents(data.agents || []);
          setIsLoadingAgents(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBackendAgents([]);
          setIsLoadingAgents(false);
        }
      });

    return () => { cancelled = true; };
  }, [sport]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);
  useEffect(() => {
    testEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [testMessages]);

  // ─── Template Selection ─────────────────────────────────────────────────

  const handleTemplateSelect = useCallback((template) => {
    setSelectedTemplate(template);
    setAgentConfig({
      name: template.name,
      description: template.desc,
      sources: [...template.sources],
      tools: [...(template.realTools || [])],
      outputs: ['chat'],
    });
    setMode('configure');
    rewardTemplateSelect();
  }, [rewardTemplateSelect]);

  // ─── Config Toggles ────────────────────────────────────────────────────

  const toggleItem = useCallback((key, id, item) => {
    // Don't toggle unavailable items
    if (item && !item.available) return;

    setAgentConfig(prev => ({
      ...prev,
      [key]: prev[key].includes(id)
        ? prev[key].filter(i => i !== id)
        : [...prev[key], id],
    }));
    setSelectedTemplate(null);
  }, []);

  // ─── Deploy Agent (activates a real backend agent) ───────────────────────

  const resolveAgent = useCallback(() => {
    const template = selectedTemplate;
    let agent = null;

    if (template?.agentId) {
      agent = backendAgents.find(a => a.id === template.agentId);
    }
    if (!agent && backendAgents.length > 0) {
      agent = backendAgents.find(a => a.sport === sport) || backendAgents[0];
    }

    if (agent) {
      return {
        id: agent.id,
        name: agentConfig.name || agent.name,
        description: agentConfig.description || agent.description,
        sport: agent.sport,
        tools: agent.tools || [],
        sources: agentConfig.sources,
        outputs: agentConfig.outputs,
        pillar: selectedTemplate?.pillar,
        model: 'gemini-3-flash-preview',
      };
    }
    return {
      id: template?.agentId || 'sports-analyst',
      name: agentConfig.name || 'Custom Agent',
      description: agentConfig.description || 'Custom coaching agent',
      sport,
      tools: agentConfig.tools,
      sources: agentConfig.sources,
      outputs: agentConfig.outputs,
      pillar: selectedTemplate?.pillar,
      model: 'gemini-3-flash-preview',
    };
  }, [selectedTemplate, backendAgents, sport, agentConfig]);

  const handleDeployAgent = useCallback(() => {
    // Check if user role needs an interview/schedule flow
    // TODO: wire real userRole from AuthContext when available
    const userRole = 'ANALYST_SCOUT';
    const deployMode = getDeployMode(userRole);

    if (deployMode === 'interview' || deployMode === 'schedule') {
      setShowDeployFlow(true);
      return;
    }

    // Immediate activation for analysts/admins
    const resolved = resolveAgent();
    setActiveAgent(resolved);
    setShowPreview(true);
    rewardAgentCreation();
  }, [resolveAgent, rewardAgentCreation]);

  const handleDeployFlowActivate = useCallback(({ agent, mode: deployMode, context, schedule }) => {
    setShowDeployFlow(false);
    const resolved = resolveAgent();
    setActiveAgent(resolved);
    setShowPreview(true);
    // If deploy had context from interview, seed the chat
    if (context) {
      setChatMessages([{ role: 'system', content: context }]);
    }
    setMode('chat');
    rewardAgentCreation();
  }, [resolveAgent, rewardAgentCreation]);

  // ─── Chat Builder (uses real /api/agent/chat SSE) ───────────────────────

  const handleChatSend = useCallback(async () => {
    if (!chatInput.trim() || isChatStreaming) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsChatStreaming(true);
    setMcpArtifacts([]); // Clear previous MCP artifacts for new message

    // Determine which agent to use — sport-aware default routing
    const SPORT_DEFAULT_AGENTS = {
      basketball: 'basketball_analyst',
      baseball: 'baseball_analyst',
      soccer: 'sports-analyst',
      hockey: 'sports-analyst',
      football: 'sports-analyst',
      golf: 'sports-analyst',
    };
    const agentId = activeAgent?.id ||
      selectedTemplate?.agentId ||
      SPORT_DEFAULT_AGENTS[sport] || 'sports-analyst';

    setChatMessages(prev => [...prev, {
      role: 'assistant',
      content: '',
      isStreaming: true,
    }]);

    // Capture and clear attachments before sending
    const pendingAttachments = chatAttachments.length > 0
      ? chatAttachments.map(a => ({ uri: a.gcsUri, type: a.mimeType, name: a.filename }))
      : undefined;
    setChatAttachments([]);

    abortRef.current = await streamAgentChat({
      message: userMessage,
      agentId,
      sport,
      sessionId: chatSessionId,
      attachments: pendingAttachments,
      onText: (chunk, full) => {
        setChatMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && last.role === 'assistant') {
            updated[updated.length - 1] = { ...last, content: full, isStreaming: true };
          }
          return updated;
        });
      },
      onToolCall: (event) => {
        setChatMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && last.role === 'assistant') {
            updated[updated.length - 1] = { ...last, toolCall: event.tool };
          }
          return updated;
        });
      },
      onToolResult: (event) => {
        if (event.mcpApp) {
          setMcpArtifacts(prev => [...prev, {
            uri: event.mcpApp.uri,
            title: event.mcpApp.title,
            tool: event.tool,
            toolResult: event.toolResult,
          }]);
        }
      },
      onAgent: (event) => {
        // If we haven't activated an agent yet, activate the routed one
        if (!activeAgent && event.agentId) {
          const foundAgent = backendAgents.find(a => a.id === event.agentId);
          if (foundAgent) {
            setActiveAgent({
              id: foundAgent.id,
              name: foundAgent.name,
              description: foundAgent.description,
              sport: foundAgent.sport,
              tools: foundAgent.tools || [],
              model: 'gemini-3.1-pro-preview',
            });
          }
        }
      },
      onDone: (sid) => {
        setChatSessionId(sid);
        setChatMessages(prev => {
          const updated = [...prev];
          if (updated.length > 0) {
            updated[updated.length - 1] = {
              ...updated[updated.length - 1],
              isStreaming: false,
            };
          }
          return updated;
        });
      },
      onError: (err) => {
        setChatMessages(prev => {
          const updated = [...prev];
          if (updated.length > 0 && updated[updated.length - 1].role === 'assistant') {
            updated[updated.length - 1] = {
              role: 'assistant',
              content: `Error: ${err}`,
              isStreaming: false,
            };
          } else {
            updated.push({ role: 'assistant', content: `Error: ${err}` });
          }
          return updated;
        });
      },
    });

    setIsChatStreaming(false);
  }, [chatInput, isChatStreaming, activeAgent, selectedTemplate, sport, chatSessionId, backendAgents]);

  // ─── Test Agent (preview panel SSE) ──────────────────────────────────────

  const handleTestSend = useCallback(async () => {
    if (!testInput.trim() || isTestStreaming || !activeAgent) return;

    const userMessage = testInput.trim();
    setTestInput('');
    setTestMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsTestStreaming(true);
    setTestMessages(prev => [...prev, {
      role: 'assistant',
      content: '',
      isStreaming: true,
    }]);

    await streamAgentChat({
      message: userMessage,
      agentId: activeAgent.id,
      sport,
      sessionId: testSessionId,
      onText: (chunk, full) => {
        setTestMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && last.role === 'assistant') {
            updated[updated.length - 1] = { ...last, content: full, isStreaming: true };
          }
          return updated;
        });
      },
      onToolCall: (event) => {
        setTestMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && last.role === 'assistant') {
            updated[updated.length - 1] = { ...last, toolCall: event.tool };
          }
          return updated;
        });
      },
      onDone: (sid) => {
        setTestSessionId(sid);
        setTestMessages(prev => {
          const updated = [...prev];
          if (updated.length > 0) {
            updated[updated.length - 1] = {
              ...updated[updated.length - 1],
              isStreaming: false,
            };
          }
          return updated;
        });
      },
      onError: (err) => {
        setTestMessages(prev => {
          const updated = [...prev];
          if (updated.length > 0 && updated[updated.length - 1].role === 'assistant') {
            updated[updated.length - 1] = {
              role: 'assistant',
              content: `Error: ${err}`,
              isStreaming: false,
            };
          } else {
            updated.push({ role: 'assistant', content: `Error: ${err}` });
          }
          return updated;
        });
      },
    });

    setIsTestStreaming(false);
  }, [testInput, isTestStreaming, activeAgent, sport, testSessionId]);

  // ─── Reset ─────────────────────────────────────────────────────────────

  const handleReset = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setSelectedTemplate(null);
    setAgentConfig({ name: '', description: '', sources: [], tools: [], outputs: ['chat'] });
    setActiveAgent(null);
    setChatMessages([]);
    setTestMessages([]);
    setChatSessionId(null);
    setTestSessionId(null);
    setShowPreview(false);
  }, []);

  // ─── Key handlers ────────────────────────────────────────────────────────

  const handleChatKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChatSend();
    }
  }, [handleChatSend]);

  const handleTestKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleTestSend();
    }
  }, [handleTestSend]);

  // ─── Derived ───────────────────────────────────────────────────────────

  const templates = getTemplatesForSport(sport);
  const sources = getSourcesForSport(sport);
  const tools = getToolsForSport(sport);

  // Pillar-based groupings for Configure tab (tools filtered by sport)
  const allConfigItems = [
    ...sources.map(s => ({ ...s, _type: 'source' })),
    ...tools.map(t => ({ ...t, _type: 'tool' })),
    ...OUTPUT_CHANNELS.map(o => ({ ...o, _type: 'output' })),
  ];
  const itemsByPillar = groupByPillar(allConfigItems);

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full" style={{ background: C.bg }}>
      {/* ── Top Bar ─────────────────────────────────────────────────── */}
      <div
        className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{ background: C.s1, borderBottom: `1px solid ${C.border}` }}
      >
        <div className="flex items-center gap-3">
          <Bot className="w-5 h-5" style={{ color: C.gold }} />
          <span className="text-base font-semibold" style={{ color: C.gold }}>
            Coaching Canvas
          </span>
          <span className="text-xs" style={{ color: C.t3 }}>Agent Builder</span>
        </div>

        <div className="flex items-center gap-2">
          {/* Sport Selector */}
          <div className="flex rounded-full p-0.5" style={{ background: C.s2, border: `1px solid ${C.border}` }}>
            {SPORT_OPTIONS.map(s => (
              <button
                key={s.id}
                onClick={() => { setSport(s.id); handleReset(); }}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs transition-all"
                style={{
                  background: sport === s.id ? C.g08 : 'transparent',
                  color: sport === s.id ? C.gold : C.t3,
                  fontWeight: sport === s.id ? 500 : 300,
                  borderBottom: sport === s.id ? `2px solid ${C.gold}` : '2px solid transparent',
                }}
                title={`${s.name}${s.hasRealAgents ? '' : ' (coming soon)'}`}
              >
                {s.emoji}
                {sport === s.id && (
                  <span className="text-[10px]">{s.name}</span>
                )}
              </button>
            ))}
          </div>

          {/* Mode Selector */}
          <div className="flex rounded-full p-0.5" style={{ background: C.s2, border: `1px solid ${C.border}` }} data-tour="canvas-mode">
            {[
              { id: 'templates', label: 'Playbook' },
              { id: 'configure', label: 'Configure' },
              { id: 'build', label: 'Build Agent' },
              { id: 'chat', label: 'Chat' },
              { id: 'develop', label: 'Develop' },
            ].map(m => (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                className="px-3 py-1 rounded-full text-xs transition-all"
                style={{
                  background: mode === m.id ? C.g08 : 'transparent',
                  color: mode === m.id ? C.gold : C.t3,
                  fontWeight: mode === m.id ? 500 : 300,
                }}
              >
                {m.label}
              </button>
            ))}
          </div>

          {/* Deploy / Status */}
          {(agentConfig.sources.length > 0 || agentConfig.name) && !activeAgent && (
            <button
              onClick={handleDeployAgent}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-colors"
              style={{ background: C.g08, border: `1px solid ${C.goldDim}`, color: C.gold }}
            >
              <Sparkles className="w-3 h-3" /> Deploy Agent
            </button>
          )}
          {activeAgent && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs"
              style={{ background: 'rgba(88,214,141,0.08)', border: '1px solid rgba(88,214,141,0.3)', color: '#58d68d' }}>
              <Check className="w-3 h-3" /> {activeAgent.name}
            </span>
          )}
          {(selectedTemplate || agentConfig.sources.length > 0 || activeAgent) && (
            <button onClick={handleReset}
              className="px-3 py-1.5 rounded-full text-xs transition-colors hover:bg-white/5"
              style={{ border: '1px solid rgba(231,76,60,0.2)', color: '#e74c3c' }}>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Progress Breadcrumb ────────────────────────────────────── */}
      <div className="flex items-center gap-1 px-4 py-1.5 flex-shrink-0"
        style={{ background: C.s1, borderBottom: `1px solid ${C.border}`, height: 28 }}>
        {[
          { label: '1. Pick Template', done: !!selectedTemplate },
          { label: '2. Configure', done: agentConfig.sources.length > 0 || agentConfig.tools.length > 0 },
          { label: '3. Build', done: mode === 'build' || mode === 'chat' || mode === 'develop' },
          { label: '4. Test', done: !!activeAgent || chatMessages.length > 0 },
          { label: '5. Deploy', done: !!activeAgent },
        ].map((step, i, arr) => (
          <React.Fragment key={step.label}>
            <span
              className="text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1"
              style={{
                background: step.done ? C.g08 : 'transparent',
                color: step.done ? C.gold : C.t3,
                fontWeight: step.done ? 500 : 300,
              }}
            >
              {step.done && <Check className="w-2.5 h-2.5" />}
              {step.label}
            </span>
            {i < arr.length - 1 && (
              <ChevronRight className="w-2.5 h-2.5" style={{ color: C.t3 }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ── Main Content ──────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Left: Main Canvas Area ─────────────────────────────── */}
        <div className="flex-1 overflow-auto" style={{ background: C.bg }}>
          <div className="p-6 max-w-5xl mx-auto">

            {/* ── MODE: TEMPLATES ─────────────────────────────────── */}
            {mode === 'templates' && (
              <div>
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-extralight mb-2" style={{ color: C.text }}>
                    Choose Your Play
                  </h1>
                  <p className="text-sm" style={{ color: C.t2 }}>
                    Pre-built agent templates for {SPORT_OPTIONS.find(s => s.id === sport)?.name || sport}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-tour="canvas-templates">
                  {templates.map(t => {
                    const TIcon = getIcon(t.icon);
                    const hasBackendAgent = backendAgents.some(a => a.id === t.agentId);
                    return (
                      <button
                        key={t.id}
                        onClick={() => handleTemplateSelect(t)}
                        className="text-left p-5 rounded-xl transition-all hover:-translate-y-0.5 group"
                        style={{ background: C.s1, border: `1px solid ${C.border}` }}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <TIcon className="w-5 h-5" style={{ color: C.gold }} />
                          {hasBackendAgent && (
                            <span className="text-[8px] px-1.5 py-0.5 rounded-full"
                              style={{ background: 'rgba(88,214,141,0.1)', color: '#58d68d', border: '1px solid rgba(88,214,141,0.2)' }}>
                              LIVE
                            </span>
                          )}
                        </div>
                        <h3 className="text-sm font-medium mb-1 group-hover:text-amber-400 transition-colors"
                          style={{ color: C.text }}>{t.name}</h3>
                        <p className="text-xs mb-3" style={{ color: C.t2 }}>{t.desc}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {t.sources.slice(0, 3).map(sid => {
                            const src = DATA_SOURCES.find(s => s.id === sid);
                            return src ? (
                              <span key={sid} className="text-[10px] px-2 py-0.5 rounded-md"
                                style={{
                                  background: src.available ? C.g08 : 'transparent',
                                  border: `1px solid ${src.available ? C.goldDim : C.border}`,
                                  color: src.available ? C.gold : C.t3,
                                  opacity: src.available ? 1 : 0.4,
                                }}>
                                {src.name}
                              </span>
                            ) : null;
                          })}
                          {t.sources.length > 3 && (
                            <span className="text-[10px]" style={{ color: C.t3 }}>
                              +{t.sources.length - 3}
                            </span>
                          )}
                        </div>
                        {t.sport !== 'all' && (
                          <span className="inline-block mt-2 text-[9px] px-1.5 py-0.5 rounded"
                            style={{ background: C.s3, color: C.t3 }}>
                            {t.sport}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Live backend agents */}
                {backendAgents.length > 0 && (
                  <div className="mt-10">
                    <h2 className="text-[10px] font-semibold uppercase tracking-widest mb-3"
                      style={{ color: C.t3 }}>Live Agents ({backendAgents.length})</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {backendAgents.map(a => (
                        <div key={a.id}
                          className="flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-white/[0.02] transition-colors group"
                          style={{ background: C.s1, border: `1px solid ${C.border}` }}
                          onClick={() => {
                            setActiveAgent({
                              id: a.id,
                              name: a.name,
                              description: a.description,
                              sport: a.sport,
                              tools: a.tools || [],
                              model: 'gemini-3.1-pro-preview',
                            });
                            setShowPreview(true);
                          }}
                        >
                          <div className="flex-1 min-w-0">
                            <span className="text-xs font-medium" style={{ color: C.text }}>
                              {a.name}
                            </span>
                            <p className="text-[11px] truncate mt-0.5" style={{ color: C.t2 }}>
                              {a.description}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 ml-3">
                            <span className="text-[9px] px-1.5 rounded" style={{ background: C.s3, color: C.t3 }}>
                              {a.sport || 'all'}
                            </span>
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#58d68d' }} />
                            <ChevronRight className="w-3.5 h-3.5" style={{ color: C.t3 }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {isLoadingAgents && (
                  <div className="flex items-center justify-center py-8 gap-2" style={{ color: C.t3 }}>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-xs">Loading agents...</span>
                  </div>
                )}
              </div>
            )}

            {/* ── MODE: CONFIGURE ─────────────────────────────────── */}
            {mode === 'configure' && (
              <div>
                {/* Agent Identity */}
                <div className="p-5 rounded-xl mb-6" style={{ background: C.s1, border: `1px solid ${C.border}` }} data-tour="agent-identity">
                  <div className="flex items-center gap-3 mb-3">
                    <Bot className="w-5 h-5" style={{ color: C.gold }} />
                    <input
                      value={agentConfig.name}
                      onChange={e => setAgentConfig(p => ({ ...p, name: e.target.value }))}
                      placeholder="Name your agent..."
                      className="flex-1 bg-transparent text-lg font-light outline-none"
                      style={{ color: C.gold, borderBottom: `1px solid ${C.border}` }}
                    />
                  </div>
                  <textarea
                    value={agentConfig.description}
                    onChange={e => setAgentConfig(p => ({ ...p, description: e.target.value }))}
                    placeholder="Describe what this agent should do..."
                    rows={2}
                    className="w-full bg-transparent text-xs font-light outline-none resize-none"
                    style={{ color: C.t2 }}
                  />
                </div>

                {/* Deploy flow (role-adaptive) */}
                {showDeployFlow && (
                  <div className="mb-6">
                    <AgentDeployFlow
                      agent={resolveAgent()}
                      sport={sport}
                      userRole="HEAD_COACH"
                      onActivate={handleDeployFlowActivate}
                      onCancel={() => setShowDeployFlow(false)}
                    />
                  </div>
                )}

                {/* Pillar columns: Acquisition | Development | Scouting */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {['acquisition', 'development', 'scouting'].map(pillarId => {
                    const pillar = PILLAR_CONFIG[pillarId];
                    const items = itemsByPillar[pillarId] || [];
                    const pillarSources = items.filter(i => i._type === 'source');
                    const pillarTools = items.filter(i => i._type === 'tool');
                    const pillarOutputs = items.filter(i => i._type === 'output');
                    const PillarIcon = getIcon(pillar.icon);

                    return (
                      <div key={pillarId}
                        className="p-4 rounded-xl"
                        style={{ background: C.s1, border: `1px solid ${C.border}` }}
                        data-tour={`pillar-${pillarId}`}
                      >
                        {/* Pillar header */}
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-5 h-5 rounded flex items-center justify-center"
                            style={{ background: `${pillar.color}15` }}>
                            <PillarIcon className="w-3 h-3" style={{ color: pillar.color }} />
                          </div>
                          <span className="text-[10px] font-semibold uppercase tracking-widest"
                            style={{ color: pillar.color }}>
                            {pillar.label}
                          </span>
                          <span className="text-[8px] ml-auto px-1.5 py-0.5 rounded"
                            style={{ background: `${pillar.color}10`, color: pillar.color }}>
                            {pillar.shortLabel}
                          </span>
                        </div>
                        <p className="text-[10px] mb-3" style={{ color: C.t3 }}>{pillar.desc}</p>

                        {/* Sources in this pillar */}
                        {pillarSources.length > 0 && (
                          <div className="mb-3">
                            <span className="text-[8px] font-semibold uppercase tracking-wider block mb-1.5"
                              style={{ color: C.t3 }}>Sources</span>
                            <div className="flex flex-wrap gap-1.5">
                              {pillarSources.map(s => (
                                <ConfigChip
                                  key={s.id}
                                  item={s}
                                  active={agentConfig.sources.includes(s.id)}
                                  pillarColor={pillar.color}
                                  onClick={() => toggleItem('sources', s.id, s)}
                                />
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Tools in this pillar */}
                        {pillarTools.length > 0 && (
                          <div className="mb-3">
                            <span className="text-[8px] font-semibold uppercase tracking-wider block mb-1.5"
                              style={{ color: C.t3 }}>Tools</span>
                            <div className="flex flex-wrap gap-1.5">
                              {pillarTools.map(t => (
                                <ConfigChip
                                  key={t.id}
                                  item={t}
                                  active={agentConfig.tools.includes(t.id)}
                                  pillarColor={pillar.color}
                                  onClick={() => toggleItem('tools', t.id, t)}
                                />
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Outputs in this pillar */}
                        {pillarOutputs.length > 0 && (
                          <div>
                            <span className="text-[8px] font-semibold uppercase tracking-wider block mb-1.5"
                              style={{ color: C.t3 }}>Outputs</span>
                            <div className="flex flex-wrap gap-1.5">
                              {pillarOutputs.map(o => (
                                <ConfigChip
                                  key={o.id}
                                  item={o}
                                  active={agentConfig.outputs.includes(o.id)}
                                  pillarColor={pillar.color}
                                  onClick={() => toggleItem('outputs', o.id, o)}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Utility items (not pillar-aligned) */}
                {(itemsByPillar.utility || []).length > 0 && (
                  <div className="mt-4 p-4 rounded-xl" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
                    <div className="flex items-center gap-2 mb-3">
                      <Wrench className="w-3 h-3" style={{ color: C.t3 }} />
                      <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: C.t3 }}>
                        Utility
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(itemsByPillar.utility || []).map(item => (
                        <ConfigChip
                          key={item.id}
                          item={item}
                          active={
                            item._type === 'source' ? agentConfig.sources.includes(item.id) :
                            item._type === 'tool' ? agentConfig.tools.includes(item.id) :
                            agentConfig.outputs.includes(item.id)
                          }
                          pillarColor="#999"
                          onClick={() => toggleItem(
                            item._type === 'source' ? 'sources' : item._type === 'tool' ? 'tools' : 'outputs',
                            item.id, item
                          )}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Summary + Deploy */}
                <div className="mt-6 p-4 rounded-xl flex items-center justify-between"
                  style={{ background: C.s1, border: `1px solid ${C.border}` }}
                  data-tour="config-summary"
                >
                  <div className="flex items-center gap-4 text-[10px]" style={{ color: C.t3 }}>
                    <span>{agentConfig.sources.length} sources</span>
                    <span>{agentConfig.tools.length} tools</span>
                    <span>{agentConfig.outputs.length} outputs</span>
                    <span className="px-1.5 rounded" style={{ background: C.s3 }}>{sport}</span>
                  </div>
                  <div className="flex items-center gap-2" data-tour="deploy-button">
                    {(agentConfig.sources.length > 0 || agentConfig.name) && !activeAgent && (
                      <button
                        onClick={handleDeployAgent}
                        className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-colors"
                        style={{ background: C.g08, border: `1px solid ${C.goldDim}`, color: C.gold }}
                      >
                        <Sparkles className="w-3 h-3" /> Deploy Agent
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── MODE: BUILD AGENT (Visual Flow) ─────────────────── */}
            {mode === 'build' && (
              <div data-tour="flow-canvas">
                <AgentFlowBuilder sport={sport} initialConfig={agentConfig} />
              </div>
            )}

            {/* ── MODE: CHAT BUILDER ──────────────────────────────── */}
            {mode === 'chat' && (
              <div className="flex h-[calc(100vh-120px)]">
                {/* Main chat column */}
                <div className="flex-1 flex flex-col min-w-0">
                  <div className="text-center mb-4">
                    <h1 className="text-2xl font-extralight mb-1" style={{ color: C.text }}>
                      {activeAgent ? activeAgent.name : 'Talk to Your Agent'}
                    </h1>
                    <p className="text-xs" style={{ color: C.t2 }}>
                      {activeAgent
                        ? `Connected to ${activeAgent.name} — ask anything about your ${sport} data`
                        : 'Select a template or just start chatting. SLAM will route to the best agent.'}
                    </p>
                  </div>

                  {/* Chat messages */}
                  <div className="flex-1 overflow-auto mb-4 space-y-3 px-2">
                    {chatMessages.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-16">
                        <Bot className="w-12 h-12 mb-4" style={{ color: C.t3 }} />
                        <p className="text-sm mb-6" style={{ color: C.t2 }}>
                          {activeAgent ? `Ask ${activeAgent.name} anything` : 'What do you want to know?'}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl">
                          {(selectedTemplate?.starterPrompts || DEFAULT_PROMPTS[sport] || DEFAULT_PROMPTS.basketball).map((prompt, i) => (
                            <button key={i}
                              onClick={() => { setChatInput(prompt); setTimeout(() => chatInputRef.current?.focus(), 0); }}
                              className="text-left text-xs p-3 rounded-lg transition-colors hover:bg-white/[0.03]"
                              style={{ background: C.s1, border: `1px solid ${C.border}`, color: C.t2 }}
                            >
                              {prompt}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {chatMessages.map((msg, i) => (
                      <ChatBubble key={i} message={msg} />
                    ))}

                    {/* MCP App Visualizations */}
                    {mcpArtifacts.map((artifact, i) => (
                      <div key={`mcp-${i}`} className="mx-2 mb-3 rounded-xl overflow-hidden"
                        style={{ border: `1px solid ${C.borderLight}` }}>
                        <div className="px-3 py-1.5 text-xs font-medium flex items-center gap-1.5"
                          style={{ background: C.s2, color: C.t2, borderBottom: `1px solid ${C.borderLight}` }}>
                          <BarChart3 className="w-3.5 h-3.5" style={{ color: C.gold }} />
                          {artifact.title}
                        </div>
                        <MCPAppRenderer
                          resourceUri={artifact.uri}
                          toolName={artifact.tool}
                          toolResult={artifact.toolResult}
                          height={360}
                        />
                      </div>
                    ))}

                    <div ref={chatEndRef} />
                  </div>

                  {/* Chat input with attachment support */}
                  <div className="relative px-2">
                    <CanvasChatAttachment
                      attachments={chatAttachments}
                      onAttach={(a) => setChatAttachments(prev => [...prev, a])}
                      onRemove={(i) => setChatAttachments(prev => prev.filter((_, idx) => idx !== i))}
                    />
                    <div className="flex gap-2">
                      <textarea
                        ref={chatInputRef}
                        value={chatInput}
                        onChange={e => setChatInput(e.target.value)}
                        onKeyDown={handleChatKeyDown}
                        placeholder={activeAgent
                          ? `Ask ${activeAgent.name} anything...`
                          : 'Ask a question — SLAM will route to the best agent...'}
                        rows={1}
                        className="flex-1 px-4 py-2.5 rounded-xl text-sm resize-none outline-none"
                        style={{ background: C.s2, border: `1px solid ${C.borderLight}`, color: C.text }}
                      />
                      <button
                        onClick={handleChatSend}
                        disabled={(!chatInput.trim() && chatAttachments.length === 0) || isChatStreaming}
                        className="px-4 rounded-xl transition-colors disabled:opacity-30"
                        style={{ background: C.g08, border: `1px solid ${C.goldDim}`, color: C.gold }}
                      >
                        {isChatStreaming
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Send className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Agent Flow Minimap — collapsible side panel */}
                {activeAgent && (
                  <AgentFlowMinimap agent={activeAgent} />
                )}
              </div>
            )}

            {/* ── MODE: DEVELOPER PORTAL ──────────────────────────── */}
            {mode === 'develop' && (
              <DeveloperPortal sport={sport} />
            )}
          </div>
        </div>

        {/* ── Right: Agent Preview Panel ──────────────────────────── */}
        {showPreview && activeAgent && (
          <div className="w-96 flex flex-col flex-shrink-0"
            style={{ background: C.s1, borderLeft: `1px solid ${C.border}` }}>
            {/* Preview header */}
            <div className="flex items-center justify-between px-4 py-3"
              style={{ borderBottom: `1px solid ${C.border}` }}>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" style={{ color: C.gold }} />
                <span className="text-xs font-medium" style={{ color: C.gold }}>
                  {activeAgent.name}
                </span>
              </div>
              <button onClick={() => setShowPreview(false)} className="p-1 rounded hover:bg-white/5">
                <X className="w-3.5 h-3.5" style={{ color: C.t3 }} />
              </button>
            </div>

            {/* Agent info */}
            <div className="px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
              <p className="text-[11px] mb-2" style={{ color: C.t2 }}>
                {activeAgent.description}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {(activeAgent.tools || []).slice(0, 6).map(tool => (
                  <span key={tool} className="text-[9px] px-1.5 py-0.5 rounded"
                    style={{ background: C.s3, color: C.t3 }}>
                    {tool}
                  </span>
                ))}
                {(activeAgent.tools || []).length > 6 && (
                  <span className="text-[9px]" style={{ color: C.t3 }}>
                    +{activeAgent.tools.length - 6}
                  </span>
                )}
                <span className="text-[9px] px-1.5 py-0.5 rounded"
                  style={{ background: C.g08, color: C.gold }}>
                  {activeAgent.model || 'gemini-3-flash'}
                </span>
                <span className="text-[9px] px-1.5 py-0.5 rounded"
                  style={{ background: C.s3, color: C.t3 }}>
                  {activeAgent.sport || sport}
                </span>
              </div>
            </div>

            {/* Starter prompts */}
            {testMessages.length === 0 && selectedTemplate?.starterPrompts && (
              <div className="px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
                <span className="text-[9px] font-semibold uppercase tracking-widest mb-2 block"
                  style={{ color: C.t3 }}>Try asking</span>
                <div className="space-y-1.5">
                  {selectedTemplate.starterPrompts.map((p, i) => (
                    <button key={i}
                      onClick={() => { setTestInput(p); }}
                      className="block w-full text-left text-[11px] p-2 rounded-lg hover:bg-white/[0.03] transition-colors"
                      style={{ color: C.t2, border: `1px solid ${C.border}` }}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Test messages */}
            <div className="flex-1 overflow-auto px-4 py-3 space-y-3">
              {testMessages.map((msg, i) => (
                <ChatBubble key={i} message={msg} compact />
              ))}
              <div ref={testEndRef} />
            </div>

            {/* Test input */}
            <div className="flex gap-2 px-3 py-3" style={{ borderTop: `1px solid ${C.border}` }}>
              <input
                value={testInput}
                onChange={e => setTestInput(e.target.value)}
                onKeyDown={handleTestKeyDown}
                placeholder="Test your agent..."
                className="flex-1 px-3 py-2 rounded-lg text-xs outline-none"
                style={{ background: C.s2, border: `1px solid ${C.borderLight}`, color: C.text }}
              />
              <button
                onClick={handleTestSend}
                disabled={!testInput.trim() || isTestStreaming}
                className="px-3 rounded-lg transition-colors disabled:opacity-30"
                style={{ background: C.g08, color: C.gold }}
              >
                {isTestStreaming
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Send className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Guided Tour Tooltips */}
      {[canvasTour, configureTour, buildTour].map((tour, i) =>
        tour.currentStep ? (
          <GuidedTooltip
            key={i}
            targetSelector={tour.currentStep.targetSelector}
            title={tour.currentStep.title}
            body={tour.currentStep.body}
            placement={tour.currentStep.placement}
            step={tour.stepIndex + 1}
            totalSteps={tour.totalSteps}
            onNext={tour.next}
            onDismiss={tour.dismiss}
          />
        ) : null
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function ConfigChip({ item, active, pillarColor, onClick }) {
  const ItemIcon = getIcon(item.icon);
  const disabled = !item.available;
  const firstAvailable = active && item.available;

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] transition-all"
      style={{
        background: active ? `${pillarColor}12` : 'transparent',
        border: `1px solid ${active ? `${pillarColor}40` : C.border}`,
        color: active ? pillarColor : C.t3,
        opacity: disabled ? 0.25 : (active ? 1 : 0.7),
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      title={disabled ? `${item.desc} (not connected)` : item.desc}
      disabled={disabled}
      data-tour={firstAvailable ? 'source-available' : undefined}
    >
      {disabled ? <Lock className="w-2.5 h-2.5" /> : <ItemIcon className="w-3 h-3" />}
      {item.name}
    </button>
  );
}

function ChatBubble({ message, compact = false }) {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';

  // System messages (context from deploy interview) render as subtle info
  if (isSystem) {
    return (
      <div className="flex justify-center">
        <div className="px-3 py-1.5 rounded-full text-[10px]"
          style={{ background: C.s2, color: C.t3, border: `1px solid ${C.border}` }}>
          {message.content}
        </div>
      </div>
    );
  }

  // Sanitized markdown HTML for assistant messages (safeMd uses DOMPurify)
  const renderedHtml = !isUser ? safeMd(message.content) : '';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`${compact ? 'max-w-[280px]' : 'max-w-[600px]'} px-3 py-2 rounded-xl text-xs leading-relaxed`}
        style={{
          background: isUser ? C.g08 : C.s2,
          border: `1px solid ${isUser ? C.goldDim : C.border}`,
          color: isUser ? C.gold : C.text,
        }}
      >
        {message.isStreaming && !message.content && (
          <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: C.t3 }} />
        )}
        {message.toolCall && (
          <div className="flex items-center gap-1.5 mb-1.5 text-[10px]" style={{ color: C.t3 }}>
            <Wrench className="w-3 h-3" /> Using {message.toolCall}...
          </div>
        )}
        {isUser ? (
          <div className="whitespace-pre-wrap">{message.content}</div>
        ) : (
          <div
            className="prose prose-xs max-w-none dark:prose-invert [&_p]:text-xs [&_li]:text-xs [&_h1]:text-sm [&_h2]:text-xs [&_h3]:text-xs [&_code]:text-[10px] [&_pre]:text-[10px]"
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
          />
        )}
      </div>
    </div>
  );
}
